import { useMemo, useState } from "react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { arrayValue, intValue, mapValue, stringValue } from "@/utils/coerce";
import { formatMs, formatUsd } from "@/utils/format";
import {
  laneTree,
  promptBindingsOf,
  type NodeTokens,
  type NormalizedTrace,
  type PromptBinding,
  type TraceNode,
} from "@/utils/trace/model";
import { PromptBindingBadge, TurnFlag } from "./PromptBindingBadge";

/**
 * What one model call cost, broken down as far as the record allows.
 *
 * Deliberately not the waterfall re-coloured. A prompt's parts are not events
 * in time — they are the composition of a single input — so a stacked bar says
 * what a timeline cannot: which block of the prompt the tokens actually went to.
 *
 * Two things here are estimates and are drawn as such. The per-part sizes come
 * from a local tokenizer, not from the provider, so their sum never quite
 * matches the real prompt count; the difference is drawn as its own segment
 * rather than folded silently into the parts. And a node with no recorded parts
 * — an older turn, a sub-agent round, a citation span — degrades to the four
 * counts every provider reports, which is the honest floor.
 */

const SEGMENT_CLASS = {
  cached: "bg-trace-wait",
  input: "bg-trace-inference",
  // Prompt tokens too, so the prompt's own hue — hatched, because they were
  // billed above the input rate (a cache write). No new colour: the palette is
  // validated as a set (see index.css), and pattern carries the difference.
  cacheWrite:
    "bg-trace-inference bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgb(255_255_255/45%)_3px,rgb(255_255_255/45%)_6px)]",
  // Only ever the difference between the local estimate and the provider's real
  // count. A node with no recorded parts made no estimate, so nothing about it
  // is unattributed — its prompt takes the `input` colour like any other. Grey
  // means "미완료·중단" everywhere else in this UI and must not leak onto a turn
  // that completed perfectly well.
  unattributed: "bg-trace-idle",
  output: "bg-trace-tool",
  thoughts: "bg-trace-subagent",
} as const;

type SegmentKey = keyof typeof SEGMENT_CLASS;

/** Matches the waterfall's own indent, so one nesting level looks the same in both. */
const DEPTH_INDENT_PX = 14;

type Segment = {
  key: string;
  kind: SegmentKey;
  label: string;
  tokens: number;
};

type Row = {
  node: TraceNode;
  laneLabel: string;
  /** The lane's task id. Four delegated districts are otherwise four identical rows. */
  laneSublabel?: string;
  /** How deep the lane sits, so a delegated round reads as inside its turn. */
  depth: number;
  tokens: NodeTokens;
  total: number;
  segments: Segment[];
  contextWindowTokens: number | null;
  promptBindings: PromptBinding[];
  isFinalizer: boolean;
  /** Recorded as an empty list — the turn ran with no tools at all. */
  noToolsRegistered: boolean;
};

/** Prompt blocks the backend measured for this turn, largest first. */
function promptParts(detail: Record<string, unknown>): Segment[] {
  return arrayValue(mapValue(detail.request_params).prompt_parts)
    .map((raw) => {
      const part = mapValue(raw);
      return {
        name: stringValue(part.name, ""),
        tokens: intValue(part.est_tokens) ?? 0,
      };
    })
    .filter((part) => part.name && part.tokens > 0)
    .sort((a, b) => b.tokens - a.tokens)
    .map((part) => ({
      key: `part:${part.name}`,
      kind: "input" as const,
      label: part.name,
      tokens: part.tokens,
    }));
}

function rowFor(
  node: TraceNode,
  lane: { label: string; sublabel?: string },
  depth: number,
): Row | null {
  const tokens = node.tokens;
  if (!tokens) return null;
  const total =
    tokens.total ??
    (tokens.input ?? 0) + (tokens.output ?? 0) + (tokens.thoughts ?? 0);
  if (total <= 0) return null;

  const detail = node.detail;
  const input = tokens.input ?? 0;
  // Split the way the backend bills it (credits.partition_prompt_tokens):
  // reads first, then writes, each clamped so no token is drawn twice.
  const cached = Math.min(tokens.cachedInput ?? 0, input);
  const written = Math.min(tokens.cacheWriteInput ?? 0, input - cached);
  const parts = promptParts(detail);
  // Whatever the parts do not account for. On a turn with no parts recorded
  // this is the entire uncached prompt, which is exactly the degraded view.
  const uncachedInput = Math.max(input - cached - written, 0);
  const attributed = parts.reduce((sum, part) => sum + part.tokens, 0);
  const unattributed = Math.max(uncachedInput - attributed, 0);

  const segments: Segment[] = [
    ...(cached > 0
      ? [{ key: "cached", kind: "cached" as const, label: "cached", tokens: cached }]
      : []),
    ...(written > 0
      ? [
          {
            key: "cacheWrite",
            kind: "cacheWrite" as const,
            label: "cache write",
            tokens: written,
          },
        ]
      : []),
    // Normally identity — `unattributed` is exactly what the parts left over,
    // so the factor works out to one. It bites only when the local estimate
    // OVERSHOOTS the provider's count: there is no shortfall to draw, and the
    // parts are scaled down to the tokens actually charged rather than summing
    // past the bar they sit in.
    ...parts.map((part) => ({
      ...part,
      tokens: Math.round((part.tokens / Math.max(attributed, 1)) * (uncachedInput - unattributed)),
    })),
    ...(unattributed > 0
      ? [
          {
            key: "unattributed",
            // With no parts recorded there was no estimate to be off by: this
            // is simply the prompt, and colouring it as a shortfall would read
            // as something having gone wrong.
            kind: parts.length > 0 ? ("unattributed" as const) : ("input" as const),
            label: parts.length > 0 ? "미귀속 (추정 오차)" : "prompt",
            tokens: unattributed,
          },
        ]
      : []),
    ...((tokens.output ?? 0) > 0
      ? [
          {
            key: "output",
            kind: "output" as const,
            label: "output",
            tokens: tokens.output as number,
          },
        ]
      : []),
    ...((tokens.thoughts ?? 0) > 0
      ? [
          {
            key: "thoughts",
            kind: "thoughts" as const,
            label: "thinking",
            tokens: tokens.thoughts as number,
          },
        ]
      : []),
  ];

  const generation = mapValue(detail.generation_params);
  const registered = detail.registered_tool_names;

  return {
    node,
    laneLabel: lane.label,
    laneSublabel: lane.sublabel,
    depth,
    tokens,
    total,
    segments,
    contextWindowTokens:
      intValue(mapValue(detail.request_params).context_window_tokens) ??
      intValue(mapValue(mapValue(detail.request_params).context_budget).context_window_tokens),
    promptBindings: promptBindingsOf(detail),
    isFinalizer: generation.is_finalizer === true,
    noToolsRegistered: Array.isArray(registered) && registered.length === 0,
  };
}

function TokenBar({ segments, total }: { segments: Segment[]; total: number }) {
  return (
    <div className="flex h-4 w-full overflow-hidden rounded-sm bg-surface-subtle">
      {segments.map((segment) => (
        <span
          key={segment.key}
          data-segment={segment.kind}
          title={`${segment.label} · ${segment.tokens.toLocaleString()}`}
          className={SEGMENT_CLASS[segment.kind]}
          style={{ width: `${(segment.tokens / total) * 100}%` }}
        />
      ))}
    </div>
  );
}

export function TraceTokens({
  trace,
  selected,
  onSelect,
  openBinding,
  onOpenBinding,
}: {
  trace: NormalizedTrace;
  selected: TraceNode | null;
  onSelect: (node: TraceNode) => void;
  /** Independent of `selected`: a badge must not close an open node detail. */
  openBinding?: PromptBinding | null;
  /** Absent when the mount cannot fetch — badges then render as plain text. */
  onOpenBinding?: (binding: PromptBinding) => void;
}) {
  const [byCost, setByCost] = useState(false);

  const rows = useMemo(() => {
    const out: Row[] = [];
    for (const { lane, depth } of laneTree(trace.lanes)) {
      const walk = (nodes: TraceNode[]) => {
        for (const node of nodes) {
          const row = rowFor(node, lane, depth);
          if (row) out.push(row);
          walk(node.children);
        }
      };
      walk(lane.nodes);
    }
    return out;
  }, [trace.lanes]);

  const ordered = useMemo(
    () =>
      byCost
        ? [...rows].sort((a, b) => b.total - a.total)
        : [...rows].sort(
            (a, b) =>
              (a.node.startMs ?? Number.MAX_SAFE_INTEGER) -
              (b.node.startMs ?? Number.MAX_SAFE_INTEGER),
          ),
    [rows, byCost],
  );

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        이 트레이스에는 기록된 토큰 사용량이 없습니다.
      </p>
    );
  }

  return (
    <div className="grid gap-3" data-testid="trace-tokens">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {/* Two buttons rather than one that flips its label. A single toggle was
            wrong twice over: it did not read as something you could press, and
            its label named the CURRENT state while looking like the action it
            would take, so it said the opposite of what it did. */}
        <span className="flex items-center gap-1.5">
          정렬
          {([
            ["time", "시간 순"],
            ["cost", "토큰 순"],
          ] as const).map(([key, label]) => (
            <Button
              key={key}
              aria-pressed={byCost === (key === "cost")}
              variant={byCost === (key === "cost") ? "default" : "outline"}
              onClick={() => setByCost(key === "cost")}
            >
              {label}
            </Button>
          ))}
        </span>
        <LegendDot swatch={SEGMENT_CLASS.cached} label="cached" />
        <LegendDot swatch={SEGMENT_CLASS.cacheWrite} label="cache write" />
        <LegendDot swatch={SEGMENT_CLASS.input} label="prompt" />
        <LegendDot swatch={SEGMENT_CLASS.unattributed} label="미귀속 (추정 오차)" />
        <LegendDot swatch={SEGMENT_CLASS.output} label="output" />
        <LegendDot swatch={SEGMENT_CLASS.thoughts} label="thinking" />
      </div>

      {ordered.map((row) => (
        <div
          key={row.node.id}
          data-token-row
          data-depth={row.depth}
          className={cn(
            "grid gap-1 rounded-sm",
            row.node.id === selected?.id && "bg-surface-subtle",
          )}
          // Indented the way the waterfall indents its lanes, from the same
          // tree, so a delegated round still reads as running inside the turn
          // that spawned it rather than as its sibling.
          style={row.depth > 0 ? { marginLeft: row.depth * DEPTH_INDENT_PX } : undefined}
        >
          {/* A row of sibling controls, not one button wrapping the lot. The
              badges below open their own thing, and a button inside a button
              is both invalid and — as it shipped — indistinguishable from
              clicking the row. */}
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 px-1 text-[13px]">
          <button
            type="button"
            onClick={() => onSelect(row.node)}
            aria-pressed={row.node.id === selected?.id}
            className="flex cursor-pointer flex-wrap items-baseline gap-x-2.5 gap-y-1 rounded-sm text-left hover:bg-surface-subtle"
          >
            {/* When it ran, so the ordering survives sorting by cost — and so a
                round inside a turn's wait window can be placed at all. */}
            <span className="w-28 shrink-0 tabular-nums text-xs text-muted-foreground">
              {row.node.startMs === null ? "—" : `+${formatMs(row.node.startMs)}`}
              {row.node.durationMs !== null && ` · ${formatMs(row.node.durationMs)}`}
            </span>
            <span className="font-medium">{row.node.label}</span>
            <span className="text-xs text-muted-foreground">{row.laneLabel}</span>
            {row.laneSublabel && (
              <span className="font-mono text-xs text-muted-foreground">
                {row.laneSublabel}
              </span>
            )}
            {row.node.model && (
              <span className="font-mono text-xs text-muted-foreground">
                {row.node.model}
              </span>
            )}
          </button>
          {/* Which prompt version each block came from — the axis a prompt
              regression is diagnosed on, and now what opens that version. */}
          {row.promptBindings.map((binding) => (
            <PromptBindingBadge
              key={binding.id}
              binding={binding}
              open={openBinding?.id === binding.id}
              onOpen={onOpenBinding}
            />
          ))}
          {row.isFinalizer && (
            // The last round runs with its tools stripped and a nudge appended,
            // so the same model and prompt version answer differently there.
            // Nothing else in the row would show it.
            <TurnFlag>finalizer</TurnFlag>
          )}
          {row.noToolsRegistered && (
            // "had no tools" is not "chose not to call one", and the tool calls
            // alone cannot tell them apart.
            <TurnFlag>도구 없음</TurnFlag>
          )}
          <span className="ml-auto tabular-nums">
            {row.total.toLocaleString()} tok
            {/* What the provider billed, when it says (OpenRouter). Tokens
                alone hide the cache: a read costs a tenth, a write 1.25x. */}
            {row.tokens.costUsd !== null && (
              <span className="text-muted-foreground"> · {formatUsd(row.tokens.costUsd)}</span>
            )}
          </span>
          </div>
          <TokenBar segments={row.segments} total={row.total} />
          <div className="flex flex-wrap gap-x-2.5 gap-y-1 text-xs text-muted-foreground">
            {row.segments.map((segment) => (
              <span key={segment.key} className="inline-flex items-center gap-1">
                <span
                  aria-hidden
                  className={cn(
                    "size-1.5 shrink-0 rounded-full",
                    SEGMENT_CLASS[segment.kind],
                  )}
                />
                {segment.label} {segment.tokens.toLocaleString()}
              </span>
            ))}
            {row.contextWindowTokens !== null && row.tokens.input !== null && (
              <span>
                context window {row.contextWindowTokens.toLocaleString()} 중{" "}
                {((row.tokens.input / row.contextWindowTokens) * 100).toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function LegendDot({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("inline-block h-2.5 w-2.5 rounded-sm", swatch)} />
      {label}
    </span>
  );
}
